import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SportsEsportsRoundedIcon from "@mui/icons-material/SportsEsportsRounded";
import { useEffect, useMemo, useState } from "react";
import { AvailableGamesResult } from "../interface/SocketResponse";
import type { ActiveRoomInfo } from "../context/UserContext";
import { SESSION_APPBAR_TOOLBAR_PX } from "./SessionAppBar";
import { usePostHog } from "@posthog/react";

type Props = {
  games: AvailableGamesResult[];
  onFormSubmit: (value: string) => void;
  connectionError?: string | null;
  /** ECS wake in progress before bootstrap socket connects. */
  serverWaking?: boolean;
  onRetryWake?: () => void;
  activeRooms?: ActiveRoomInfo[];
  showResumeColumn?: boolean;
  onResumeRoom?: (room: ActiveRoomInfo) => void;
  onRefreshResume?: () => void | Promise<void>;
  displayNameForGreeting: string;
  isFirstLogin?: boolean;
  /** After a bad invite link while signed in (e.g. room no longer exists). */
  inviteJoinNotice?: string | null;
};

export const NamespaceSelector = ({
  games,
  onFormSubmit,
  connectionError,
  serverWaking = false,
  onRetryWake,
  activeRooms = [],
  showResumeColumn = false,
  onResumeRoom,
  onRefreshResume,
  displayNameForGreeting,
  isFirstLogin = false,
  inviteJoinNotice = null,
}: Props) => {
  const posthog = usePostHog();
  const [slowConnection, setSlowConnection] = useState(false);

  /** Full table / account display name — do not take only the first word (e.g. "The great one"). */
  const greetingName = useMemo(() => displayNameForGreeting.trim(), [displayNameForGreeting]);

  const roomsByGame = useMemo(() => {
    const map = new Map<string, ActiveRoomInfo[]>();
    for (const r of activeRooms) {
      const list = map.get(r.gameTitle) ?? [];
      list.push(r);
      map.set(r.gameTitle, list);
    }
    const ordered: { title: string; rooms: ActiveRoomInfo[] }[] = [];
    for (const g of games) {
      const list = map.get(g.title);
      if (list?.length) ordered.push({ title: g.title, rooms: list });
    }
    for (const [title, rooms] of Array.from(map.entries())) {
      if (!ordered.some((o) => o.title === title)) ordered.push({ title, rooms });
    }
    return ordered;
  }, [activeRooms, games]);

  const resumeCountsByNamespace = useMemo(() => {
    const counts = new Map<string, number>();
    for (const room of activeRooms) {
      counts.set(room.namespace, (counts.get(room.namespace) ?? 0) + 1);
    }
    return counts;
  }, [activeRooms]);

  useEffect(() => {
    if (games.length > 0 || connectionError || serverWaking) return;
    const timer = setTimeout(() => setSlowConnection(true), 4000);
    return () => clearTimeout(timer);
  }, [games.length, connectionError, serverWaking]);

  useEffect(() => {
    if (!showResumeColumn || !onRefreshResume) return;
    void onRefreshResume();
  }, [showResumeColumn, onRefreshResume]);

  const onClick = (endpoint: string, gameTitle: string) => {
    posthog?.capture("game_selected", { game: gameTitle, endpoint });
    onFormSubmit(endpoint);
  };

  const gameMeta = (title: string) => {
    const t = title.toLowerCase();
    if (t.includes("addata")) return { players: "4-12 players", subtitle: "Classic team-based card game", beta: false };
    if (t.includes("literature")) return { players: "6 players", subtitle: "Strategic team card collection", beta: true };
    if (t.includes("rummy")) return { players: "2-6 players", subtitle: "Form sets and sequences", beta: true };
    return { players: "Multiplayer", subtitle: "Join and play with friends", beta: false };
  };

  const resumeColumn =
    showResumeColumn ? (
      <Box
        sx={{
          width: { xs: "100%", md: 200 },
          flexShrink: 0,
          px: { xs: 2.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          borderBottom: { xs: 1, md: 0 },
          borderBottomStyle: "solid",
          borderColor: "divider",
          borderRight: { md: 1 },
          borderRightStyle: { md: "solid" },
          bgcolor: { md: "rgba(255,255,255,0.03)" },
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ letterSpacing: "0.14em", fontWeight: 600, display: "block", mb: 1.5 }}
        >
          RESUME
        </Typography>
        {activeRooms.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.5 }}>
            Nothing here yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {roomsByGame.map((group) => (
              <Box key={group.title}>
                <Typography
                  variant="caption"
                  sx={{ color: "secondary.main", fontWeight: 600, display: "block", mb: 0.5 }}
                >
                  {group.title}
                </Typography>
                <Stack spacing={0}>
                  {group.rooms.map((room) => (
                    <ListItemButton
                      key={`${room.namespace}-${room.roomId}`}
                      dense
                      onClick={() => {
                      posthog?.capture("active_room_resumed", { game: room.gameTitle, room_id: room.roomId, phase: room.phase });
                      onResumeRoom?.(room);
                    }}
                      sx={{
                        borderRadius: 1,
                        py: 0.75,
                        px: 1,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemText
                        primary={room.roomId}
                        secondary={room.phase === "playing" ? "In progress" : "Lobby"}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: "caption", color: "textSecondary" }}
                      />
                    </ListItemButton>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        display: "block",
        minHeight: {
          xs: `calc(100vh - ${SESSION_APPBAR_TOOLBAR_PX.xs}px)`,
          sm: `calc(100vh - ${SESSION_APPBAR_TOOLBAR_PX.sm}px)`,
        },
        width: "100%",
        boxSizing: "border-box",
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 2, sm: 3 },
        pb: { xs: "max(16px, env(safe-area-inset-bottom))", sm: 3 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch" }}>
          {resumeColumn}
          <Box sx={{ flex: 1, minWidth: 0, px: { xs: 0.5, sm: 1, md: 1.5 }, py: { xs: 1, sm: 1.5 } }}>
            <Typography
              component="h1"
              variant="h5"
              color="primary"
              sx={{
                fontWeight: 600,
                letterSpacing: "-0.02em",
                mb: 1,
                fontSize: { xs: "1.9rem", sm: "2.4rem" },
                color: "#fff",
              }}
            >
              {greetingName ? (
                <>
                  {isFirstLogin ? "Welcome, " : "Welcome back, "}
                  <Box component="span" sx={{ color: "secondary.main", wordBreak: "break-word" }}>
                    {greetingName}
                  </Box>
                </>
              ) : (
                "Welcome"
              )}
            </Typography>

            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.58)", mb: 2.5 }}>
              {activeRooms.length > 0
                ? "Choose a game to start playing or jump back into your active rooms"
                : "Choose a game to start playing"}
            </Typography>
            {inviteJoinNotice ? (
              <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
                {inviteJoinNotice}
              </Alert>
            ) : null}
            {games.length === 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                <CircularProgress size={32} color="secondary" />
                <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ maxWidth: 320 }}>
                  {serverWaking
                    ? "Starting game server… This can take up to 3 minutes when the server was stopped."
                    : connectionError
                      ? connectionError
                      : slowConnection
                        ? "Still connecting…"
                        : "Connecting…"}
                </Typography>
                {connectionError && onRetryWake ? (
                  <Button variant="outlined" color="secondary" size="small" onClick={onRetryWake}>
                    Retry
                  </Button>
                ) : null}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                  gap: 2,
                }}
              >
                {games.map((game) => {
                  const meta = gameMeta(game.title);
                  const resumeCount = resumeCountsByNamespace.get(game.endpoint) ?? 0;
                  return (
                    <Paper
                      key={game.title}
                      elevation={0}
                      onClick={() => onClick(game.endpoint, game.title)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onClick(game.endpoint, game.title);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      sx={{
                        borderRadius: 2.2,
                        p: 1.6,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background:
                          "linear-gradient(140deg, rgba(56,142,60,0.2) 0%, rgba(18,18,18,0.95) 40%, rgba(18,18,18,0.95) 100%)",
                        cursor: "pointer",
                        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: "0 10px 26px rgba(0,0,0,0.34)",
                          borderColor: "rgba(255,255,255,0.24)",
                        },
                        "&:active": {
                          transform: "scale(0.995)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid rgba(129,199,132,0.8)",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: "rgba(76,175,80,0.9)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <SportsEsportsRoundedIcon sx={{ color: "#0c0f0d", fontSize: 22 }} />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          {resumeCount > 0 ? (
                            <Chip
                              size="small"
                              label={`Resume ${resumeCount}`}
                              sx={{ fontWeight: 700, bgcolor: "rgba(129,199,132,0.22)", color: "#c8f7cc" }}
                            />
                          ) : null}
                          {meta.beta ? <Chip size="small" label="BETA" color="secondary" sx={{ fontWeight: 700 }} /> : null}
                        </Box>
                      </Box>
                      <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, fontSize: "2rem" }}>
                        {game.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.68)", minHeight: 40 }}>
                        {resumeCount > 0
                          ? `${meta.subtitle} • ${resumeCount} room${resumeCount === 1 ? "" : "s"} ready to rejoin`
                          : meta.subtitle}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#57d169", fontWeight: 600, display: "block", mt: 0.5, mb: 1.2 }}>
                        {meta.players}
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: "rgba(0,0,0,0.65)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 0.8,
                        }}
                      >
                        <Typography sx={{ textAlign: "center", color: "#fff", fontWeight: 700, fontSize: "0.92rem" }}>
                          Play Now
                        </Typography>
                        <ChevronRightRoundedIcon sx={{ color: "rgba(255,255,255,0.75)", flexShrink: 0, fontSize: 20, ml: 0.5 }} />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
